import { RecordFilter, RecordMode } from '@/types/api';
import DetailContent from '@/components/Record/SubjectContent/DetailContent';
import AttenderItem from '@/components/Record/SubjectContent/AttenderItem';
import Item from '@/components/Record/SubjectContent/AttachmentConnect/DataItem/Item';
import FileItem from '@/components/Record/SubjectContent/AttachmentFile/FileContent/FileItem';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import ModifyLayout from '@/components/Record/ModifyLayout';
import CustomDatePicker from '@/components/Record/SubjectContent/CustomDatePicker';
import InnerAttender from '@/components/Record/SubjectContent/InnerAttender';
import { useFormContext } from 'react-hook-form';
import ExternalAttender from '@/components/Record/SubjectContent/ExternalAttender';
import AttachmentConnect from '../../SubjectContent/AttachmentConnect';
import AttachmentFile from '@/components/Record/SubjectContent/AttachmentFile';
import OutlookButton from '@/components/Record/OutlookButton';
import useAi from '@/components/Record/hooks/useAi';
import { useTranslation } from 'react-i18next';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { formatAsDate } from '@/utils/format';
import ArrowDownIcon from '@/public/icons/record/arrow_down.svg';
import UploadingFile from '@/components/Record/TalkFLow/Contents/UploadingFile';
import { useAtomValue } from 'jotai';
import { uploadingState } from '@/recoil/atom';

interface AttachmentFilesSectionProps {
  attachments: any[];
  isOwner: boolean;
}

const AttachmentFilesSection: React.FC<AttachmentFilesSectionProps> = ({
  attachments,
  isOwner,
}) => {
  const uploadingData = useAtomValue(uploadingState);
  const { t } = useTranslation();

  return (
    <DetailContent
      iconName={'record_clip'}
      title={t('첨부 파일')}
      content={
        <>
          <UploadingFile uploadingData={uploadingData.file} />
          <ModifyLayout isOwner={isOwner} modifyComponent={<AttachmentFile />}>
            {attachments.length > 0 ? (
              <ItemContainer>
                {attachments.map((res) => (
                  <FileItem
                    key={res.uuid}
                    fileName={res.name}
                    uuid={res.uuid}
                    fileSize={res.size}
                  />
                ))}
              </ItemContainer>
            ) : (
              <StyledPlaceHolder>
                {isOwner ? t('파일을 이곳에 추가해 주세요.') : ''}
              </StyledPlaceHolder>
            )}
          </ModifyLayout>
        </>
      }
    />
  );
};

const AttachContents = () => {
  const { isOwner } = useAi();
  const { isMobilePath } = usePlatform();
  const { watch, setValue } = useFormContext<RecordFilter>();
  const { t } = useTranslation();
  const [showProperties, setShowProperties] = useState(!isMobilePath);

  const date = watch('meetingDate');
  const day = dayjs(date).format('dd'); // 한글 요일 2글자: 일월화수목금토
  const formatted = date
    ? `${formatAsDate(date)} (${day}) ${date.format('hh:mm A')}`
    : isOwner
    ? 'YYYY-MM-DD HH:MM AA'
    : '';

  const internal = watch('internal');
  const isMeetingMode = watch('mode') === RecordMode.Meeting;
  const external = watch('external');
  const relations = watch('relations');
  const attachments = watch('attachments');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: isMobilePath ? '32px 20px 24px 20px' : '32px 24px 24px 24px',
      }}
    >
      {isMeetingMode && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <OutlookButton />
          <DetailContent
            iconName={'record_calendar'}
            title={t('회의 일시')}
            content={
              <ModifyLayout
                isOwner={isOwner}
                modifyComponent={<CustomDatePicker />}
              >
                <ItemContainer>
                  <StyledDate isPlaceHolder={!date}>{formatted}</StyledDate>
                </ItemContainer>
              </ModifyLayout>
            }
          />
          {showProperties && (
            <>
              <DetailContent
                iconName={'record_people'}
                title={t('내부 참석자')}
                content={
                  <ModifyLayout
                    isOwner={isOwner}
                    modifyComponent={<InnerAttender />}
                  >
                    {internal.length > 0 ? (
                      <ItemContainer>
                        {internal.map((item) => (
                          <AttenderItem
                            key={`attender-${item.passportId}`}
                            data={item}
                          />
                        ))}
                      </ItemContainer>
                    ) : (
                      <StyledPlaceHolder>
                        {isOwner ? t('어떤 분이 회의에 참여하였나요?') : ''}
                      </StyledPlaceHolder>
                    )}
                  </ModifyLayout>
                }
              />
              <DetailContent
                iconName={'record_people'}
                title={t('외부 참석자')}
                content={
                  <ModifyLayout
                    modifyComponent={<ExternalAttender />}
                    isOwner={isOwner}
                  >
                    {external.length > 0 ? (
                      <ItemContainer>
                        {external.map((item) => (
                          <AttenderItem key={`attender-${item}`} title={item} />
                        ))}
                      </ItemContainer>
                    ) : (
                      <StyledPlaceHolder>
                        {isOwner
                          ? t('외부 참석자를 엔터로 구분해 입력해 주세요')
                          : ''}
                      </StyledPlaceHolder>
                    )}
                  </ModifyLayout>
                }
              />
              <DetailContent
                iconName={'record_file'}
                title={t('자료 연결')}
                content={
                  <ModifyLayout
                    isOwner={isOwner}
                    modifyComponent={<AttachmentConnect />}
                  >
                    {relations.length > 0 ? (
                      <ItemContainer>
                        {relations.map((res) => (
                          <Item key={res.id} item={res} />
                        ))}
                      </ItemContainer>
                    ) : (
                      <StyledPlaceHolder>
                        {isOwner ? t('품의나 문서를 검색해 주세요.') : ''}
                      </StyledPlaceHolder>
                    )}
                  </ModifyLayout>
                }
              />
              <AttachmentFilesSection
                attachments={attachments}
                isOwner={isOwner}
              />
            </>
          )}
        </div>
      )}
      {!isMeetingMode && (
        <AttachmentFilesSection attachments={attachments} isOwner={isOwner} />
      )}
      {isMobilePath && isMeetingMode && (
        <ToggleButton onClick={() => setShowProperties(!showProperties)}>
          <ArrowIcon isOpen={showProperties}>
            <ArrowDownIcon />
          </ArrowIcon>
          <span>{showProperties ? '속성 숨기기' : '속성 보기'}</span>
        </ToggleButton>
      )}
    </div>
  );
};
export default AttachContents;

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const StyledDate = styled.div<{ isPlaceHolder: boolean }>`
  color: ${({ isPlaceHolder }) => (isPlaceHolder ? 'var(--dk-text-dim, #B7BBC2)' : 'balck')};
`;

const StyledPlaceHolder = styled.div`
  color: var(--dk-text-dim, #b7bbc2);
  text-align: left;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px; /* 200% */
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--dk-text-secondary, #888d96);
  padding: 0 8px;
`;

const ArrowIcon = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;
